import { Grid, Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Button, Pill, Tab } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import RedirectTo404 from '@feathr/extender/App/Routes/RedirectTo404';
import { useLocalUrl } from '@feathr/extender/state';

import type { TConversionKind } from '../ReportsConversionsGoalManagerPage.hooks';
import GoalDetailDefinition from './GoalDetailDefinition';
import GoalDetailEmpty from './GoalDetailEmpty';
import GoalDetailOptions from './GoalDetailOptions';
import { useGoalDetail } from './GoalDetailPage.hooks';
import { formatGoalSubtitle } from './GoalDetailPage.utils';
import GoalDetailSegmentation from './GoalDetailSegmentation';
import GoalDetailStats from './GoalDetailStats';

function GoalDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { goalId } = useParams<{ goalId: string }>();
  const localUrl = useLocalUrl();
  const [kind, setKind] = useState<TConversionKind>('campaign');
  const {
    goal,
    primaryOnCount,
    trackedOnCount,
    totalConversions,
    totalConversionValue,
    uniqueConverters,
    conversionsDelta,
    valueDelta,
    uniqueConvertersDelta,
  } = useGoalDetail(goalId);

  function handleCampaigns(): void {
    setKind('campaign');
  }

  function handleFlights(): void {
    setKind('flight');
  }

  if (!goal.isPending && goal.isErrored) {
    return <RedirectTo404 />;
  }

  const isArchived = goal.get('is_archived');
  const convertersUrl = localUrl(`/reports/conversions/goal-manager/${goalId}/converters`);
  const hasNoConverters = uniqueConverters === 0;

  return (
    <Page
      actions={[
        <GoalDetailOptions goal={goal} key={'goal-options'} />,
        <Button
          disabled={hasNoConverters}
          key={'view-converters'}
          link={convertersUrl}
          tooltip={hasNoConverters ? t('Goal has no converters') : undefined}
          type={'primary'}
        >
          {t('View converters')}
        </Button>,
      ]}
      collapseHeader={false}
      description={
        isArchived ? (
          <Pill theme={'neutral'} variant={'secondary'}>
            {t('Archived')}
          </Pill>
        ) : (
          <Pill theme={'cyan'} variant={'secondary'}>
            {t('Active')}
          </Pill>
        )
      }
      loading={goal.isPending}
      secondaryText={formatGoalSubtitle(goal, t)}
      tabs={[
        <Tab
          active={kind === 'campaign'}
          key={'campaigns'}
          onClick={handleCampaigns}
          title={t('Campaigns')}
        />,
        <Tab
          active={kind === 'flight'}
          key={'flights'}
          onClick={handleFlights}
          title={t('Flights')}
        />,
      ]}
      title={goal.get('name')}
    >
      {totalConversions === 0 ? (
        <GoalDetailEmpty
          dashboardUrl={localUrl('/reports/conversions/dashboard')}
          kind={kind}
          trackedOnCount={trackedOnCount}
        />
      ) : (
        <Stack>
          <GoalDetailStats
            conversionsDelta={conversionsDelta}
            kind={kind}
            primaryOnCount={primaryOnCount}
            totalConversions={totalConversions}
            totalConversionValue={totalConversionValue}
            trackedOnCount={trackedOnCount}
            uniqueConverters={uniqueConverters}
            uniqueConvertersDelta={uniqueConvertersDelta}
            valueDelta={valueDelta}
          />
          <Grid>
            <Grid.Col span={8}>
              {/* TODO: Top contributing ${kind}s table */}
              {null}
            </Grid.Col>
            <Grid.Col span={4}>
              <Stack>
                <GoalDetailDefinition goal={goal} />
                <GoalDetailSegmentation
                  convertersUrl={convertersUrl}
                  uniqueConverters={uniqueConverters}
                />
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      )}
    </Page>
  );
}

export default observer(GoalDetailPage);
